import {ArchiveRecord} from 'archive/ArchiveConstants';
import {SchemaCollaborator} from 'auth/PermissionConstants';
import {LegacyAssaySchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {AssaySchemaSpec, SchemaSpec} from 'schemas/spec/SchemaConfigConstants';
import EntityStore from 'stores/EntityStore';
import * as net from 'util/Net';

import {CollaboratorInfoGraphQL} from '../../../graphql-fragments/auth/__generated/CollaboratorInfoGraphQL';
import {AssaySchema} from './AssaySchemaConstants';

function _urlPath(schemaType: string): string {
  switch (schemaType) {
    case LegacyAssaySchemaType.ASSAY_REQUEST:
      return 'requests';
    case LegacyAssaySchemaType.ASSAY_RESULT:
      return 'results';
    case LegacyAssaySchemaType.ASSAY_RUN:
      return 'runs';
    default:
      throw new Error(`Unexpected assay schema type "${schemaType}"`);
  }
}

export function assaySchemasURL(schemaType: string): string {
  return `/${EntityStore.currentUser().url_identifier}/assay-schemas/${_urlPath(schemaType)}/`;
}

export function assaySchemaURL(
  schemaType: LegacyAssaySchemaType,
  schemaId: string,
  isSpecJsonFormat?: boolean
): string {
  return isSpecJsonFormat &&
    [LegacyAssaySchemaType.ASSAY_RESULT, LegacyAssaySchemaType.ASSAY_RUN].includes(schemaType)
    ? `/${EntityStore.currentUser().url_identifier}/assay-schemas/${_urlPath(schemaType)}/json/${schemaId}`
    : `/${EntityStore.currentUser().url_identifier}/assay-schemas/${_urlPath(schemaType)}/${schemaId}`;
}

export function createAssaySchemaURL(schemaType: string): string {
  return `/${EntityStore.currentUser().url_identifier}/assay-schemas/${_urlPath(schemaType)}/create`;
}

export function createAssaySchemaURLJSON(schemaType: string): string {
  return `/${EntityStore.currentUser().url_identifier}/assay-schemas/${_urlPath(schemaType)}/json/create`;
}

export function runSchemaURL(schemaId: string): string {
  return assaySchemaURL(LegacyAssaySchemaType.ASSAY_RUN, schemaId, false);
}

export function runSchemaURLJSON(schemaId: string): string {
  return assaySchemaURL(LegacyAssaySchemaType.ASSAY_RUN, schemaId, true);
}

export function createRunSchemaURLJSON(): string {
  return createAssaySchemaURLJSON(LegacyAssaySchemaType.ASSAY_RUN);
}

export function resultSchemaURL(schemaId: string): string {
  return assaySchemaURL(LegacyAssaySchemaType.ASSAY_RESULT, schemaId, false);
}

export function resultSchemaURLJSON(schemaId: string): string {
  return assaySchemaURL(LegacyAssaySchemaType.ASSAY_RESULT, schemaId, true);
}

export function createResultSchemaURLJSON(): string {
  return createAssaySchemaURLJSON(LegacyAssaySchemaType.ASSAY_RESULT);
}

export interface FetchAllResponse {
  data: Array<AssaySchema>;
}

export function fetchAll(): JQueryPromise<FetchAllResponse> {
  return net.getJSON('/1/api/result-schemas');
}

interface CreateAssaySchemaResponse {
  assaySchema: AssaySchema;
  schemaSpec: SchemaSpec;
}

export function createAssaySchema({
  ownerId,
  name,
  spec,
  schemaType,
  sqlIdentifier,
  sessionId,
  isSpecJsonFormat = true,
  publishDefaultDataTableConfig = false,
  collaborators = undefined,
}: {
  ownerId: string;
  name: string;
  spec: SchemaSpec;
  schemaType: string;
  sqlIdentifier: string | null;
  sessionId: string;
  isSpecJsonFormat?: boolean;
  publishDefaultDataTableConfig?: boolean;
  collaborators?: Array<SchemaCollaborator>;
}): JQueryPromise<CreateAssaySchemaResponse> {
  return net.postJSON({
    url: '/1/api/result-schemas',
    data: {
      ownerId,
      name,
      spec,
      schemaType,
      sqlIdentifier,
      isSpecJsonFormat,
      publishDefaultDataTableConfig,
      collaborators,
      sessionId,
    },
  });
}

interface UpdateAssaySchemaResponse {
  assaySchema: AssaySchema;
  schemaSpec: SchemaSpec;
}

export function updateAssaySchema({
  schemaId,
  schemaName,
  spec,
  sqlIdentifier,
  sessionId,
  isSpecJsonFormat = true,
  publishDefaultDataTableConfig = false,
}: {
  schemaId: string;
  schemaName: string;
  spec: SchemaSpec;
  sqlIdentifier: string | null;
  sessionId: string;
  isSpecJsonFormat?: boolean;
  publishDefaultDataTableConfig?: boolean;
}): Promise<UpdateAssaySchemaResponse> {
  return net.patchJSONPromise({
    url: `/1/api/result-schemas/${schemaId}`,
    data: {schemaName, spec, isSpecJsonFormat, sqlIdentifier, sessionId, publishDefaultDataTableConfig},
  });
}

interface FetchSchemaSpecResponse {
  assaySchema: AssaySchema;
  schemaSpec: AssaySchemaSpec;
}

export function fetchSchemaSpec(
  schemaId: string,
  isSpecJsonFormat: boolean = true
): JQueryPromise<FetchSchemaSpecResponse> {
  return net.getJSON(
    `/1/api/result-schemas/${schemaId}/spec?isSpecJsonFormat=${isSpecJsonFormat ? 'true' : 'false'}`
  );
}

interface BulkArchiveResponse {
  archivedItemIds: Array<string>;
  archiveRecordsByItemId: {[id: string]: ArchiveRecord};
}

export function bulkArchive(ids: Array<string>, purpose: string): Promise<BulkArchiveResponse> {
  return net.postJSONPromise({
    url: '/1/api/result-schemas:bulk-archive',
    data: {ids, purpose},
  });
}

interface BulkUnarchiveResponse {
  unarchivedItemIds: Array<string>;
}

export function bulkUnarchive(ids: Array<string>): Promise<BulkUnarchiveResponse> {
  return net.postJSONPromise({
    url: '/1/api/result-schemas:bulk-unarchive',
    data: {ids},
  });
}

export interface UpdateSchemaCollaboratorsRequest {
  collaborators: Array<SchemaCollaborator>;
}

interface UpdateSchemaCollaboratorsResponse {
  schema: CollaboratorInfoGraphQL;
}

export function updateCollaborators(
  schemaId: string,
  data: UpdateSchemaCollaboratorsRequest
): Promise<UpdateSchemaCollaboratorsResponse> {
  return net.patchJSONPromise({url: `/1/api/results/${schemaId}/collaborators`, data});
}
